import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

import { wagmiConfig } from '@shared/common/providers/Web3Provider/wagmi';
import { DEFAULT_TARGET_CHAIN, tokensInfo } from '@shared/constants';
import { concatCurrencyToAmount, weiToNumber } from '@shared/helpers/web3/formatters';

import { formatReward } from '@ui/uikit/utils';

import { RAFFLE_CONTRACT } from '../contract';
import { Web3RaffleSupportedToken } from '../supportedTokens';
import { getQueryKey } from './config';
import { AmountInfo } from './types';

type RaffleTicketPriceResponse = AmountInfo | null;

type Props = {
  tokenName: Web3RaffleSupportedToken;
};

const getTicketPrice = async ({ tokenName }: Props): Promise<RaffleTicketPriceResponse> => {
  const price = await readContract(wagmiConfig, {
    ...RAFFLE_CONTRACT,
    chainId: DEFAULT_TARGET_CHAIN.id,
    functionName: tokenName === 'SQR' ? 'getTicketPriceInSqr' : 'ticketPriceUsdt',
  });

  const { decimals, symbol } = tokensInfo[tokenName];

  const priceNumber = weiToNumber(price, decimals);

  return {
    wei: price,
    value: priceNumber,
    formatted: concatCurrencyToAmount(formatReward(priceNumber), symbol),
  };
};

export const useTicketPrice = (
  tokenName: Web3RaffleSupportedToken,
  options?: UseQueryOptions<RaffleTicketPriceResponse>,
) => {
  return useQuery({
    queryKey: getQueryKey('ticketPrice', tokenName),
    queryFn: () => getTicketPrice({ tokenName }),
    ...options,
  });
};
